import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Button, Tab, Tabs } from "react-bootstrap";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert } from "@utils/helperFunctions";
import { useV2UpdateBlueprintMutation } from "@generated";
import BlueprintGeneralFields from "../common/BlueprintGeneralFields/BlueprintGeneralFields";
import { BlueprintFormState, defaultBlueprintFormState } from "../common/types";
import {
  useGetAccount,
  useGetBlueprint,
  useListRateTables,
  useListPaymentTerms,
  conversionAttributes,
} from "../common/api";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";
import VersionHistory from "@components/VersionHistory";
import {
  ProductsTab,
  ResourcesTab,
  GovernanceTab,
  ExpenseTab,
  ServicesTab,
} from "./tabs";
import type { ServicesTabHandle, BlueprintItemsTabHandle } from "./tabs";
import "../common/formStyle.css";
import "./style.css";

const Edit = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const blueprintId = Number(id) || 0;
  const navigate = useNavigate();

  const profServicesRef = useRef<ServicesTabHandle>(null);
  const managedServicesRef = useRef<ServicesTabHandle>(null);
  const productsRef = useRef<BlueprintItemsTabHandle>(null);
  const expenseRef = useRef<BlueprintItemsTabHandle>(null);

  const [form, setForm] = useState<BlueprintFormState>(
    defaultBlueprintFormState
  );
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showFailAlert, setShowFailAlert] = useState(false);

  const { hasPrivilege } = usePrivilegeCheck();
  const canEdit = hasPrivilege("settings.blueprints", ["manage"]);
  const canViewAuditLogs = hasPrivilege("settings.audit_logs", [
    "view",
    "manage",
  ]);
  const canManageAuditLogs = hasPrivilege("settings.audit_logs", ["manage"]);

  const { accountId, account, accountLoading } = useGetAccount();
  const { blueprint, blueprintLoading, refetch } = useGetBlueprint(blueprintId);
  const { rateTables: rateTableOptions, rateTablesLoading } =
    useListRateTables();
  const { paymentTerms: paymentTermOptions, paymentTermsLoading } =
    useListPaymentTerms();

  const [updateBlueprint] = useV2UpdateBlueprintMutation();

  useEffect(() => {
    if (
      blueprint &&
      !initialized &&
      !rateTablesLoading &&
      !paymentTermsLoading
    ) {
      setForm({
        name: blueprint.name,
        tags: blueprint.tagList,
        rateTable: blueprint.rateTableId ?? rateTableOptions[0]?.value ?? "",
        paymentTerm:
          blueprint.paymentTermId ?? paymentTermOptions[0]?.value ?? "",
        executiveSummary: blueprint.executiveSummary,
        solutionSummary: blueprint.solutionSummary,
      });
      setInitialized(true);
    }
  }, [
    blueprint,
    initialized,
    rateTableOptions,
    paymentTermOptions,
    rateTablesLoading,
    paymentTermsLoading,
  ]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      setErrorMessage("Blueprint name is required.");
      setShowFailAlert(true);
      return;
    }

    // Start with the current saved payload, apply in-memory service edits.
    let finalPayload = blueprint?.rawPayload ? [...blueprint.rawPayload] : [];
    if (profServicesRef.current) {
      finalPayload = profServicesRef.current.getPayloadUpdates(finalPayload);
    }
    if (managedServicesRef.current) {
      finalPayload = managedServicesRef.current.getPayloadUpdates(finalPayload);
    }
    if (productsRef.current) {
      finalPayload = productsRef.current.getPayloadUpdates(finalPayload);
    }
    if (expenseRef.current) {
      finalPayload = expenseRef.current.getPayloadUpdates(finalPayload);
    }

    // Update the projects item with form values for summaries, rate-table, and payment-term.
    finalPayload = finalPayload.map((item) => {
      if (item.type !== "projects") return item;
      return {
        ...item,
        attributes: {
          ...item?.attributes,
          "executive-summary": form.executiveSummary || "",
          "solution-summary": form.solutionSummary || "",
        },
        relationships: {
          ...item?.relationships,
          "rate-table": {
            data: { type: "rate-tables", id: Number(form.rateTable) },
          },
          "payment-term": {
            data: {
              type: "payment-terms",
              id: Number(form.paymentTerm),
            },
          },
        },
      };
    });

    setLoading(true);
    try {
      await updateBlueprint({
        id: String(blueprintId),
        body: {
          data: {
            id: String(blueprintId),
            type: "blueprints",
            attributes: {
              name: form.name.trim(),
              "tag-list": form.tags,
              payload: finalPayload as any,
              ...conversionAttributes(blueprint?.dataVersion),
            } as any,
            relationships: {
              account: {
                data: { type: "accounts", id: String(accountId) },
              },
            },
          },
        },
      }).unwrap();
      await refetch();
      setSuccessMessage("Blueprint saved.");
      setShowSuccessAlert(true);
    } catch (err: any) {
      setErrorMessage(
        err?.data?.errors?.[0]?.detail || "Something went wrong."
      );
      setShowFailAlert(true);
    } finally {
      setLoading(false);
    }
  };

  if (
    !blueprint ||
    accountLoading ||
    blueprintLoading ||
    rateTablesLoading ||
    paymentTermsLoading
  ) {
    return <ScopeStackSpinner />;
  }

  return (
    <div className="blueprintForm blueprintEdit legacyPageBleed">
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      {handleAlert(
        showFailAlert,
        errorMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      <div className="headerContainer">
        <div className="actionBtns">
          <Button
            className="cancelButton"
            onClick={() => navigate("/admin/blueprints")}
          >
            Cancel
          </Button>
          <h2>{blueprint ? `Edit ${blueprint.name}` : "Edit Blueprint"}</h2>
        </div>
        {canEdit && (
          <div className="actionBtns">
            <Button className="button" onClick={handleSave} disabled={loading}>
              {loading ? <SmallSpinner /> : "Save"}
            </Button>
          </div>
        )}
      </div>
      <div className="tabs">
        <Tabs
          defaultActiveKey="general"
          id="blueprint-edit-tabs"
          className={`mb-3 blueprintEditTabs${
            canViewAuditLogs ? " hasVersionHistoryTab" : ""
          }`}
        >
          <Tab
            eventKey="general"
            title="General"
            tabClassName="blueprintFormTab"
            className="blueprintFormTabContent generalInfo"
          >
            <BlueprintGeneralFields
              form={form}
              setForm={setForm}
              rateTableOptions={rateTableOptions}
              paymentTermOptions={paymentTermOptions}
              readOnly={!canEdit}
            />
          </Tab>
          <Tab
            eventKey="professional-services"
            title="Professional Services"
            tabClassName="blueprintFormTab"
            className="blueprintFormTabContent"
          >
            <ServicesTab
              ref={profServicesRef}
              blueprint={blueprint!}
              refetch={refetch}
              serviceType="professional_services"
              readOnly={!canEdit}
            />
          </Tab>
          <Tab
            eventKey="managed-services"
            title="Managed Services"
            tabClassName="blueprintFormTab"
            className="blueprintFormTabContent"
          >
            <ServicesTab
              ref={managedServicesRef}
              blueprint={blueprint!}
              refetch={refetch}
              serviceType="managed_services"
              readOnly={!canEdit}
            />
          </Tab>
          <Tab
            eventKey="products"
            title="Products"
            tabClassName="blueprintFormTab"
            className="blueprintFormTabContent"
          >
            <ProductsTab
              ref={productsRef}
              blueprint={blueprint!}
              refetch={refetch}
              readOnly={!canEdit}
            />
          </Tab>
          <Tab
            eventKey="resources"
            title="Resources"
            tabClassName="blueprintFormTab"
            className="blueprintFormTabContent"
          >
            <ResourcesTab
              blueprint={blueprint!}
              refetch={refetch}
              readOnly={!canEdit}
            />
          </Tab>
          <Tab
            eventKey="governance"
            title="Governance"
            tabClassName="blueprintFormTab"
            className="blueprintFormTabContent"
          >
            <GovernanceTab
              blueprint={blueprint!}
              refetch={refetch}
              readOnly={!canEdit}
            />
          </Tab>
          <Tab
            eventKey="expense"
            title="Expense"
            tabClassName="blueprintFormTab"
            className="blueprintFormTabContent"
          >
            <ExpenseTab
              ref={expenseRef}
              blueprint={blueprint!}
              refetch={refetch}
              readOnly={!canEdit}
            />
          </Tab>
          {canViewAuditLogs && (
            <Tab
              eventKey="version-history"
              title="Version History"
              tabClassName="blueprintFormTab"
              className="blueprintFormTabContent"
            >
              <VersionHistory
                filter={{
                  "context-type": "Blueprint",
                  "context-id": String(blueprintId),
                }}
                permission={canManageAuditLogs}
                account={account}
              />
            </Tab>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default Edit;
